import React, { useState, useEffect, useRef } from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import PostRequest from 'components/common/PostRequest.jsx';
import QueuedPostRequest from 'components/layout_and_quote_builder/utils/QueuedPostRequest.js';
import { defaultRequestQueueManager } from 'components/layout_and_quote_builder/utils/RequestQueueManager.js';
import pricedSolutionCountQueue from 'components/layout_and_quote_builder/utils/PricedSolutionCountQueue.js';
import LayoutNavSectionCombinationListList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationListList.jsx';
import ManufacturingLineCard from 'components/layout_and_quote_builder/ManufacturingLineCard.jsx';
import MachineColumn from 'components/layout_and_quote_builder/MachineColumn.jsx';
import { ProgressProvider } from 'components/layout_and_quote_builder/utils/ProgressContext.jsx';
import ProgressBarOverlay from 'components/layout_and_quote_builder/ProgressBarOverlay.jsx';
import LoadingOverlay from 'components/layout_and_quote_builder/LoadingOverlay.jsx';
import { setInCache, clearCache } from 'components/layout_and_quote_builder/utils/SolutionScoreCache.js';
import { runExclusive } from 'components/layout_and_quote_builder/utils/SectionFetchQueue.js';
import {
  readSelectedManufacturingLineSlug,
  clearSelectedManufacturingLineSlug
} from 'components/layout_and_quote_builder/utils/manufacturingLineUrlState.js';
import { clearSelectedSolution } from 'components/layout_and_quote_builder/utils/solutionUrlState.js';

const machineNotFoundFiltering = true;
const TOP_N_PER_PART = window.topNPerPart || 3;  // Number of best solutions to keep per part (configurable via ENV)

// The printing-machine types the "Printing Machines" filter offers, in listing order. Serigraphy is
// deliberately NOT offered, so serigraphy lines are never listed. Offset is the only type ticked by default.
const PRINTING_TYPE_FILTER_OPTIONS = ['offset', 'digital', 'flatbed_uv'];
const DEFAULT_PRINTING_TYPE_FILTER = ['offset'];

// The die-cutting types the "Die Cutting Machines" filter offers, in listing order (other types like Roller
// / Tiegel / Press / Rotary are not offered, so those lines are never listed). Digital is OFF by default;
// automatic, handfed and heidelberg cylinder are ON.
const DIE_CUTTING_TYPE_FILTER_OPTIONS = ['digital', 'automatic', 'handfed', 'heidelberg_cylinder'];
const DEFAULT_DIE_CUTTING_TYPE_FILTER = ['automatic', 'handfed', 'heidelberg_cylinder'];

// The admin-only ManufacturingLineFilters an administrator can switch off from the listing (a debugging aid)
// to reveal the manufacturing lines each one hides. Each key must match a ManufacturingLineFilters::*::KEY;
// adding a filter here plus its translation is all a new toggle needs (the server applies whatever is left
// enabled).
const ADMIN_MANUFACTURING_LINE_FILTERS = [
  // labelParams (read at render time) feed the label's interpolation. The area-ratio label shows the actual
  // MachineToSheetAreaRatio::MAXIMUM_RATIO (exposed as a window var on the page), so the label always matches
  // the implementation instead of hardcoding the number in the translation.
  {
    key: 'machine_to_sheet_area_ratio',
    labelKey: 'quote_builder.machine_area_ratio_filter',
    labelParams: () => ({ ratio: window.machine_area_ratio_filter_maximum }),
  },
  { key: 'smallest_die_cutting_machine_of_each_type', labelKey: 'quote_builder.smallest_die_cutting_filter' },
];

// A stable key for a Manufacturing Line List (plan) -- its entries' group_index:identifier, joined. Used to
// tell whether a plan is the selected one and to key the fetch effect on the picked plan.
const manufacturingLineListKey = (list) => (list || []).map((entry) => `${entry.group_index}:${entry.identifier}`).join('+');

const LayoutNavSectionCombinationList = ({ navSection, index, count, setSelectedList, materialLayoutDimensions}) => {
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  // Which "not found" message to show: an empty machine list (a section that cannot be produced) reads
  // differently from an empty solution set for a chosen machine.
  const [notFoundMessageKey, setNotFoundMessageKey] = useState('quote_builder.solution_cannot_be_found')
  const [errorMessage, setErrorMessage] = useState(null)
  const showAllRef = useRef(false)
  const hideMissingMachinesRef = useRef(false)
  const show_more_count = 400;

  const [navSectionCombinations, setNavSectionCombinations] = useState( [ ] )
  // The candidate ManufacturingLines grouped by sub_section (part group): "Sleeve cover", "Inner tray",
  // "Sleeve cover, Inner tray". Each group carries its part_names and the lines found from only that
  // group's members (see NavSectionCombinationsActionService#manufacturing_line_groups). The listing
  // renders one titled block per group.
  const [manufacturingLineGroups, setManufacturingLineGroups] = useState( [ ] )
  // The section's Manufacturing Line Lists: every complete way to manufacture the whole section -- one line
  // per part (cartesian across the parts), plus each all-parts combined line on its own. Each entry references
  // a line in a group by { identifier, group_index }; the full line object, its label and its priced count are
  // read from manufacturingLineGroups (see renderManufacturingLineList). Built server-side (ManufacturingLineListBuilder).
  const [manufacturingLineLists, setManufacturingLineLists] = useState( [ ] )
  // The Manufacturing Line List (plan) the user has picked -- its raw entries [{ identifier, group_index }, ...].
  // Selecting a plan searches its solutions (each part solved with its own line); null shows the listing.
  const [selectedManufacturingLineList, setSelectedManufacturingLineList] = useState(null)
  // The "Printing Machines" filter: only Manufacturing Lines whose printing machine is one of these types
  // are listed. Ticking/unticking a type re-fetches the listing. Offset only, by default.
  const [selectedPrintingTypes, setSelectedPrintingTypes] = useState(DEFAULT_PRINTING_TYPE_FILTER)
  // The "Die Cutting Machines" filter: only lines whose die-cutting machine is one of these types are listed.
  const [selectedDieCuttingTypes, setSelectedDieCuttingTypes] = useState(DEFAULT_DIE_CUTTING_TYPE_FILTER)
  // Admin-only debugging: the line filters currently switched off (their keys). Default none -- every filter
  // on, matching the normal behavior for everyone. When a filter is off, its hidden manufacturing lines
  // reappear. The disable is only honored for administrators server-side.
  const [disabledLineFilters, setDisabledLineFilters] = useState([])
  const toggleLineFilter = (key) => setDisabledLineFilters((previous) => (
    previous.includes(key) ? previous.filter((each) => each !== key) : [...previous, key]
  ))
  // True once a response reports this is a machines-first (ManufacturingLine) section. Drives whether the
  // Printing Machines filter shows -- kept in state so it survives a re-fetch and an empty ("no lines") result.
  const [isManufacturingLineSection, setIsManufacturingLineSection] = useState(false)
  // Restore any ManufacturingLine the URL remembers for this section. Only the slug is known up front;
  // the full machine details (labels, sheet sizes needed by the "back to machines" header) are filled
  // in from the solutions response once it arrives (see upgradeSelectedManufacturingLineFrom).
  const [selectedManufacturingLine, setSelectedManufacturingLine] = useState(() => {
    const slug = readSelectedManufacturingLineSlug(index)
    return slug ? { slug } : null
  })

  // Progress tracking for parts mode scoring
  const [scoringProgress, setScoringProgress] = useState({ completed: 0, total: 0, percentage: 0 });

  const lastFetchTime = useRef(0);
  // Signature of the last fetch's inputs. The 500ms throttle only skips a fetch whose inputs are IDENTICAL
  // to the last one (a spurious effect re-run); a real change -- e.g. a filter toggle -- always fetches, so
  // the listing never lags behind the checkboxes.
  const lastFetchInputsRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  // Bumped on every fetch (see fetchCombinations) so the previous fetch's per-card count polls can tell
  // they are stale and stop -- otherwise a refetch (e.g. the quantity changed) would let the old round keep
  // polling and patch a previous quantity's counts onto the current cards.
  const pricedSolutionCountsGenerationRef = useRef(0);

  // A linked section (solution_user_quote_nav_section_id) inherits its solution from another section and
  // renders nothing of its own here (see the early return below), so it must not run a solve of its own.
  const isLinkedSection = !!window.user_quote_nav_sections?.find(
    section => section.sort_order === index
  )?.solution_user_quote_nav_section_id;

  // Staggering (see SectionFetchQueue): a queued fetch holds its turn until it settles. settleQueueSlotRef
  // holds the resolver that releases the turn; the loading-watch effect calls it when loading returns to
  // false, and a safety timeout releases it if a fetch never settles so one stuck section can't block the
  // rest.
  const settleQueueSlotRef = useRef(null);
  const settleTimeoutRef = useRef(null);
  const SECTION_FETCH_TIMEOUT_MS = 180000;

  // A solutions response for a selected line also carries that line's full details. When the selection
  // was restored from the URL we only had its slug, so fill in the full object here (its labels and
  // sheet sizes drive the "back to machines" header). Keep the existing object when it is already full
  // (a fresh card pick) so this neither re-renders needlessly nor re-triggers the fetch — the fetch
  // effect depends on the slug, which does not change on this upgrade.
  const upgradeSelectedManufacturingLineFrom = (data) => {
    if (!data.selected_manufacturing_line) { return; }
    setSelectedManufacturingLine((previous) => (
      previous && previous.type ? previous : data.selected_manufacturing_line
    ));
  };

  // Score a single solution via API (uses QueuedPostRequest for batching)
  const fetchSolutionScore = (solution) => {
    return QueuedPostRequest({
      url: `/layouts-nav-section-combination-score`,
      data: {
        formula_options: window.dielineOptions,
        diecutting_result: solution.diecutting_result,
        nav_section_id: solution.nav_section_id,
        count: count,
        id: window.drawingId,
        layout_dimension_id: solution.material_layout_dimension.id
      },
      callback: () => {}  // PostRequest requires callback prop
    });
  };

  // Poll one manufacturing line's priced_solution_count until the shared solve behind it is ready, then
  // patch that count onto its card (clearing the card's spinner). The endpoint enqueues the single shared
  // solve once and returns 'processing' until it finishes; resolve() releases this line's turn in the
  // count queue whether it succeeds, fails, or the fetch was superseded. `generation` is the fetch this
  // poll belongs to: once a newer fetch bumps pricedSolutionCountsGenerationRef, this poll is stale and
  // stops -- it neither keeps hitting the server nor patches a previous quantity's count onto the cards.
  const pollPricedSolutionCount = (manufacturingLine, groupIndex, generation, resolve) => {
    const isStale = () => generation !== pricedSolutionCountsGenerationRef.current;

    const requestOnce = () => {
      if (isStale()) { resolve(); return; }

      PostRequest({
        url: `/manufacturing-line-priced-solution-count/${window.drawingId}/${index}/${count}`,
        data: {
          formula_options: window.dielineOptions,
          material_layout_dimensions: materialLayoutDimensions,
          layout_dimension_ids: materialLayoutDimensions.layout_dimension_ids,
          user_quote_id: window.user_quote_id,
          manufacturing_line_identifier: manufacturingLine.identifier,
          // The count is per part group, so tell the server WHICH group this card is in (a line listed
          // under several groups has its own count in each).
          group_index: groupIndex,
          // Same machine filters as the listing, so the counts are computed for the same lines.
          printing_type_filter: selectedPrintingTypes,
          die_cutting_type_filter: selectedDieCuttingTypes,
          // Admin-only: any listing filters switched off, so the count matches the (possibly wider) listing.
          disabled_manufacturing_line_filters: disabledLineFilters
        },
        callback: (data) => {
          if (isStale()) { resolve(); return; }

          if (data.status === 'completed') {
            // Patch the count onto this line's card in ITS group only (that group's part-scoped count).
            setManufacturingLineGroups((previousGroups) => previousGroups.map((group, currentGroupIndex) => (
              currentGroupIndex === groupIndex
                ? {
                    ...group,
                    manufacturing_lines: group.manufacturing_lines.map((line) => (
                      line.identifier === manufacturingLine.identifier
                        ? { ...line, priced_solution_count: data.priced_solution_count }
                        : line
                    ))
                  }
                : group
            )));
            resolve();
          } else if (data.status === 'failed') {
            // Leave the card countless rather than spinning forever; refetching the section retries.
            resolve();
          } else {
            setTimeout(requestOnce, 1000);
          }
        }
      });
    };
    requestOnce();
  };

  // Stage 2 of the machines-first flow: the listing rendered the cards with no counts (the count needs the
  // heavy shared solve), so fetch each card's priced_solution_count afterwards, 3 at a time through a
  // dedicated queue, each card spinning until its count lands. This is also the seam the per-line score
  // will reuse next. The polls are tagged with the current fetch generation so a later fetch supersedes them.
  const fetchPricedSolutionCounts = (groups) => {
    const generation = pricedSolutionCountsGenerationRef.current;
    groups.forEach((group, groupIndex) => {
      group.manufacturing_lines.forEach((manufacturingLine) => {
        pricedSolutionCountQueue.enqueue(() => new Promise((resolve) => {
          pollPricedSolutionCount(manufacturingLine, groupIndex, generation, resolve);
        }));
      });
    });
  };

  // Build Cartesian product of arrays
  const cartesianProduct = (arrays) => {
    return arrays.reduce((accumulator, currentArray) => {
      const result = [];
      accumulator.forEach(existingCombination => {
        currentArray.forEach(item => {
          result.push([...existingCombination, item]);
        });
      });
      return result;
    }, [[]]);
  };

  // Handle parts mode: score unique solutions, pick best N per part, build combinations
  const handlePartsMode = async (parts, solutionsByParts) => {
    // 1. Collect all unique solutions with their parts key
    const allSolutions = [];
    Object.entries(solutionsByParts).forEach(([partsKey, solutions]) => {
      solutions.forEach(solution => {
        allSolutions.push({ ...solution, partsKey });
      });
    });

    if (allSolutions.length === 0) {
      setNavSectionCombinations([]);
      setLoading(false);
      setNotFound(true);
      return;
    }

    // Initialize progress tracking
    const totalSolutions = allSolutions.length;
    let completedCount = 0;
    setScoringProgress({ completed: 0, total: totalSolutions, percentage: 0 });

    // 2. Score all solutions in parallel (QueuedPostRequest handles batching at 3 concurrent)
    const scorePromises = allSolutions.map(solution =>
      fetchSolutionScore(solution)
        .then(scoreData => {
          // Update progress
          completedCount++;
          const percentage = Math.round((completedCount / totalSolutions) * 100);
          setScoringProgress({ completed: completedCount, total: totalSolutions, percentage });

          // Cache the score data for later use by LayoutNavSectionCombination
          // Use diecutting_result.uuid as key since that's what LayoutNavSectionCombination uses for lookup
          setInCache(solution.diecutting_result.uuid, scoreData);
          return { solution, scoreData, success: true };
        })
        .catch(error => {
          // Update progress even on error
          completedCount++;
          const percentage = Math.round((completedCount / totalSolutions) * 100);
          setScoringProgress({ completed: completedCount, total: totalSolutions, percentage });

          console.error('[handlePartsMode] Error scoring solution:', error);
          return { solution, scoreData: null, success: false };
        })
    );

    const results = await Promise.all(scorePromises);

    // Reset progress when done
    setScoringProgress({ completed: 0, total: 0, percentage: 0 });

    // 3. Group scored solutions by parts
    const scoredByParts = {};
    results.forEach(({ solution, scoreData, success }) => {
      if (!success || !scoreData) {
        return;
      }
      if (!scoredByParts[solution.partsKey]) {
        scoredByParts[solution.partsKey] = [];
      }
      scoredByParts[solution.partsKey].push({
        ...solution,
        score: scoreData.score
      });
    });

    // 4. Pick best N per part (sorted by score, ascending = best first)
    // Combined solutions (covering all parts) are not limited; individual parts are limited to TOP_N_PER_PART
    const combinedKey = [...parts].sort().join(",");
    const bestByParts = {};
    Object.entries(scoredByParts).forEach(([partsKey, solutions]) => {
      const sorted = [...solutions].sort((a, b) => a.score - b.score);
      if (partsKey === combinedKey) {
        // Show all combined solutions (no limit)
        bestByParts[partsKey] = sorted;
      } else {
        // Limit individual parts to best N
        bestByParts[partsKey] = sorted.slice(0, TOP_N_PER_PART);
      }
    });

    // 5. Build combinations
    const combinations = [];

    // Add combined solutions (single-item combinations that cover all parts)
    if (bestByParts[combinedKey]) {
      bestByParts[combinedKey].forEach(solution => {
        combinations.push([solution]);
      });
    }

    // Build Cartesian product from individual part solutions (best 3 each). A section with NO individual
    // parts (parts === [], e.g. a single-part carton solved through a picked plan, whose solutions all
    // arrive under the "" combined key) has nothing to cross-combine — every solution is already emitted by
    // the combined-key path above. It must be guarded explicitly: for empty parts, partArrays is [] and
    // partArrays.every(...) is VACUOUSLY true, so cartesianProduct([]) runs and returns [[]] — one EMPTY
    // combination. That empty combination renders as a bodyless, scoreless card (its container's totalScore
    // stays 0, which sortDivs even clamps to a spurious 5.0 stars and sorts to the front). Require at least
    // one part before taking the product.
    const partArrays = parts.map(part => bestByParts[part] || []);
    const allPartsHaveSolutions = partArrays.length > 0 && partArrays.every(arr => arr.length > 0);

    if (allPartsHaveSolutions) {
      const cartesian = cartesianProduct(partArrays);
      cartesian.forEach(combination => {
        combinations.push(combination);
      });
    }

    // 6. Set combinations for display
    setNavSectionCombinations(combinations);
    setLoading(false);
    setNotFound(combinations.length === 0);
  };

  const pollForResults = (jobId) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      fetch(`/layouts-nav-section-combinations-status/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
        .then(response => response.json())
        .then(data => {
          if (data.status === 'completed') {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;

            // Fill in the selected line's full details (present on a per-line solutions response).
            upgradeSelectedManufacturingLineFrom(data);

            // Check for parts mode (new grouped format)
            if (data.has_parts) {
              handlePartsMode(data.parts, data.solutions_by_parts);
              return;
            }

            // Manufacturing-line mode: list the machine configurations as cards. Solutions for a
            // manufacturingLine are searched once it is selected (Slice 2).
            if (data.has_manufacturing_lines) {
              const manufacturingLineLists = data.manufacturing_line_lists || [];
              setManufacturingLineGroups(() => data.manufacturing_line_groups || []);
              setManufacturingLineLists(() => manufacturingLineLists);
              setIsManufacturingLineSection(() => true);
              setLoading(() => false);
              // No list means either no lines at all, or lines that cannot cover every part -- either way the
              // section cannot be manufactured, so show the "no Manufacturing Lines" message.
              setNotFoundMessageKey('quote_builder.no_manufacturing_lines');
              setNotFound(() => manufacturingLineLists.length === 0);
              fetchPricedSolutionCounts(data.manufacturing_line_groups || []);
              return;
            }

            // Standard mode (existing behavior)
            setNavSectionCombinations(() => data.solutions);
            setLoading(() => false);
            setNotFound(() => data.solutions.length === 0);

            if (data.auto_select) {
              setSelectedList(data.solutions[0]);
            }
          } else if (data.status === 'failed') {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;

            setLoading(() => false);
            setNotFound(() => true);
            console.error('Job failed:', data.error);
          }
          // If status is 'processing', continue polling
        })
        .catch(error => {
          console.error('Polling error:', error);
        });
    }, 1000);
  };

  // Returns true when it actually started a fetch, false when it skipped one (a linked section, or the
  // 500ms throttle of an IDENTICAL back-to-back fetch) — the queue uses this to know whether to wait for a
  // settle or move on.
  const fetchCombinations = () => {
    if (isLinkedSection) { return false; }

    const now = Date.now();
    // Only throttle a repeat of the SAME inputs (a spurious effect re-run); any real change -- count,
    // material dimensions, selected line, or a filter toggle -- fetches even inside the 500ms window.
    const inputsSignature = JSON.stringify({
      count,
      materialLayoutDimensions,
      slug: selectedManufacturingLine ? selectedManufacturingLine.slug : null,
      listKey: manufacturingLineListKey(selectedManufacturingLineList),
      printingTypeFilter: selectedPrintingTypes,
      dieCuttingTypeFilter: selectedDieCuttingTypes,
      disabledLineFilters
    });
    if (inputsSignature === lastFetchInputsRef.current && now - lastFetchTime.current < 500) { return false; }
    lastFetchInputsRef.current = inputsSignature;
    lastFetchTime.current = now;

    // Reset cache and progress at the start of each fetch
    // This ensures stale cached scores are not reused and the progress bar starts fresh
    clearCache();
    defaultRequestQueueManager.clear();  // Drops still-queued requests from the previous fetch (and bumps the progress generation)
    // Do the same for the per-card count fetches: drop the previous fetch's still-queued polls and bump the
    // generation so any that are already in flight stop and cannot patch a stale count (see fetchPricedSolutionCounts).
    pricedSolutionCountQueue.clear();
    pricedSolutionCountsGenerationRef.current += 1;
    setScoringProgress({ completed: 0, total: 0, percentage: 0 });

    setLoading(() => true);
    setErrorMessage(() => null);

    PostRequest({
      url: `/layouts-nav-section-combinations/${window.drawingId}/${index}/${count}`,
      data: {
        formula_options: window.dielineOptions,
        material_layout_dimensions: materialLayoutDimensions,
        layout_dimension_ids: materialLayoutDimensions.layout_dimension_ids,
        user_quote_id: window.user_quote_id,
        // Post the unique identifier when we have it (a direct card pick) so the exact machine resolves;
        // the slug is only carried for the restore-from-URL case, where it is all the URL has. The server
        // matches the identifier first (see NavSectionCombinationsActionService#selected_manufacturing_line).
        manufacturing_line_identifier: selectedManufacturingLine ? selectedManufacturingLine.identifier : undefined,
        manufacturing_line_slug: selectedManufacturingLine ? selectedManufacturingLine.slug : undefined,
        // A picked plan (Manufacturing Line List): its entries [{ identifier, group_index }, ...]. The server
        // solves each part with its own line (NavSectionCombinationsActionService#build_solutions_for_manufacturing_line_list).
        manufacturing_line_list: selectedManufacturingLineList || undefined,
        // The listing's machine filters -- the finder lists only lines whose printing / die-cutting machine
        // is one of the ticked types (see NavSectionCombinationsActionService#printing_type_filter /
        // #die_cutting_type_filter).
        printing_type_filter: selectedPrintingTypes,
        die_cutting_type_filter: selectedDieCuttingTypes,
        // Admin-only debugging: the ManufacturingLineFilters an administrator has switched off, so the lines
        // they hide reappear (honored server-side for administrators only).
        disabled_manufacturing_line_filters: disabledLineFilters
      },
      callback: (data) => {
        if (data.status === 'failed') {
          // The POST only returns 'failed' for the dev cache-disabled guard,
          // whose `error` is a curated, safe message. Do NOT route the polling
          // path's failure (which carries a raw job exception) through here, or
          // that exception text would leak into the UI.
          setLoading(() => false);
          setErrorMessage(() => data.error);
          return;
        }

        if (data.job_id && data.status === 'processing') {
          pollForResults(data.job_id);
        } else {
          // Synchronous response handling

          // Fill in the selected line's full details (present on a per-line solutions response).
          upgradeSelectedManufacturingLineFrom(data);

          // Check for parts mode (new grouped format)
          if (data.has_parts) {
            handlePartsMode(data.parts, data.solutions_by_parts);
            return;
          }

          // Manufacturing-line mode: list the machine configurations as cards. Solutions for a
          // manufacturingLine are searched once it is selected (Slice 2). The listing now returns
          // synchronously (no job) because finding the lines is solve-free; the counts are fetched after.
          if (data.has_manufacturing_lines) {
            const manufacturingLineLists = data.manufacturing_line_lists || [];
            setManufacturingLineGroups(() => data.manufacturing_line_groups || []);
            setManufacturingLineLists(() => manufacturingLineLists);
            setIsManufacturingLineSection(() => true);
            setLoading(() => false);
            // No list means either no lines at all, or lines that cannot cover every part -- either way the
            // section cannot be manufactured, so show the "no Manufacturing Lines" message.
            setNotFoundMessageKey('quote_builder.no_manufacturing_lines');
            setNotFound(() => manufacturingLineLists.length === 0);
            fetchPricedSolutionCounts(data.manufacturing_line_groups || []);
            return;
          }

          // Standard mode (existing behavior)
          setNavSectionCombinations(() => data.solutions);
          setLoading(() => false);
          setNotFound(() => data.solutions.length === 0);

          if (data.auto_select) {
            setSelectedList(data.solutions[0]);
          }
        }
      }
    });

    return true;
  };

  // Release this section's turn in the fetch queue (see SectionFetchQueue), so the next section's solve
  // can start. Safe to call more than once.
  const releaseQueueSlot = () => {
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }
    if (settleQueueSlotRef.current) {
      const resolve = settleQueueSlotRef.current;
      settleQueueSlotRef.current = null;
      resolve();
    }
  };

  // Cleanup polling interval and cache on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      // If this section unmounts mid-solve (e.g. its solution is auto-selected, which hides the list),
      // the loading-watch effect never runs, so release its queue turn here — otherwise every later
      // section waits on it until the safety timeout. Also clears the pending timeout.
      releaseQueueSlot();
      // Clear score cache to prevent memory leaks when navigating away
      clearCache();
    };
  }, []);

   const sortingInProgress = useRef(false)
   const myRef = useRef();
   const sortDivs = () => {
    if (sortingInProgress.current) return;

    sortingInProgress.current = true;

    var $items = $(myRef.current).children("div");

    let isSorted = true;
    for (let i = 0; i < $items.length - 1; i++) {
        let num_i = parseInt($($items[ i ]).attr("data-score"));
        let num_i_plus_1 = parseInt ($($items[ i+1 ]).attr("data-score"));
        if (num_i > num_i_plus_1 ) {
            isSorted = false;
            break;
        }
    }

    if (!isSorted) {
      $items.sort((a, b) => parseInt($(a).attr("data-score"))  -  parseInt( $(b).attr("data-score") ) );
      $(myRef.current).append($items);
    }

    // Calculate and add star ratings directly to DOM (no React state)
    if ($items.length > 0) {
      const scores = $items.toArray().map(item => parseInt($(item).attr("data-score"))).filter(score => !isNaN(score));
      if (scores.length > 0) {
        const minScore = Math.min(...scores.filter(s => s > 0));
        const maxScore = Math.max(...scores.filter(s => s > 0));
        const range = maxScore - minScore;

        $items.each((index, item) => {
          const score = parseInt($(item).attr("data-score"));
          if (!isNaN(score) && range > 0) {
            const normalizedScore = (score - minScore) / range;
            const starRating = 5 - (normalizedScore * 4); // Scale from 5 to 1 instead of 5 to 0
            const clampedRating = Math.max(1, Math.min(5, starRating)); // Minimum 1 star

            // Round to 1 decimal place to avoid mismatch between displayed text and star rendering
            const roundedRating = Math.round(clampedRating * 10) / 10;

            // Find or create the stars container
            let $starsContainer = $(item).find('.stars-rating-container');
            if ($starsContainer.length === 0) {
              $starsContainer = $('<span class="stars-rating-container"></span>');
              $(item).find('[data-score]').first().parent().prepend($starsContainer);
            }

            // Use manual stars for all ratings for consistent sizing
            const fullStars = Math.floor(roundedRating);
            const remainder = roundedRating - fullStars;
            const hasHalfStar = remainder >= 0.25 && remainder < 0.75;
            const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

            let starsHtml = '<span style="color: #ffc107; font-size: 14px; margin-left: 8px;">';

            // Full stars
            for (let i = 0; i < fullStars; i++) {
              starsHtml += '★';
            }

            // Half star using CSS overlay
            if (hasHalfStar) {
              starsHtml += '<span style="position: relative; display: inline-block;">' +
                '<span style="color: #ddd;">★</span>' +
                '<span style="position: absolute; left: 0; width: 50%; overflow: hidden; color: #ffc107;">★</span>' +
                '</span>';
            }

            // Empty stars
            for (let i = 0; i < emptyStars; i++) {
              starsHtml += '☆';
            }

            starsHtml += ' <span style="margin-left: 4px; color: #666; font-size: 13px;">' + roundedRating.toFixed(1) + '</span></span>';

            $starsContainer.html(starsHtml);
          }
        });
      }
    }

    // Apply visibility after sorting
    if (!myRef.current) {
      sortingInProgress.current = false;
      setTimeout(sortDivs, 200)
      return;
    }

    var $items2 = $(myRef.current).children("div");

    // Count items with missing machines (check for .missing-machine class on children)
    const itemsWithMissingMachines = $items2.filter((_, item) => $(item).find('.missing-machine').length > 0);
    const missingCount = itemsWithMissingMachines.length;

    // Update button visibility and text via jQuery
    const $buttonContainer = $(myRef.current).siblings('.' + styles.show_more_container);
    const $missingMachineButton = $buttonContainer.find('.missing-machine-button');

    if (missingCount > 0) {
      $missingMachineButton.show();
      if (hideMissingMachinesRef.current) {
        $missingMachineButton.text(t('quote_builder.show_missing_machines', { count: missingCount }));
      } else {
        $missingMachineButton.text(t('quote_builder.hide_missing_machines', { count: missingCount }));
      }
    } else {
      $missingMachineButton.hide();
    }

    // Apply visibility based on show more and hide missing machines settings
    let visibleIndex = 0;
    $items2.each((_, item) => {
      const $item = $(item);
      const hasMissingMachine = $item.find('.missing-machine').length > 0;

      // Hide if missing machines filter is active and this item has missing machines
      if (hideMissingMachinesRef.current && hasMissingMachine) {
        $item.addClass(styles.hidden_combination);
        return; // continue to next item
      }

      // Apply show_more_count limit
      if (!showAllRef.current && visibleIndex >= show_more_count) {
        $item.addClass(styles.hidden_combination);
      } else {
        $item.removeClass(styles.hidden_combination);
      }
      visibleIndex++;
    });

    sortingInProgress.current = false;
    setTimeout(sortDivs, 200)
  };

  const showNavSectionCombinationGroup = ( list ) => {
    return <LayoutNavSectionCombinationListList list={list} count={count} setSelectedList={setSelectedList} machineNotFoundFiltering={machineNotFoundFiltering} />
  }

  const randomString = Math.random().toString(36).substring(2, 10);

  // Deselect the picked line / plan and return to the machine (Manufacturing Line List) listing. The picked
  // selection itself is shown on top of the solutions as its own reused card (see renderSelectedManufacturingCard),
  // so this is just the control that goes back.
  const renderBackToMachines = () => {
    if (selectedManufacturingLine) {
      return (
        <button
          onClick={() => { clearSelectedManufacturingLineSlug(index); clearSelectedSolution(index); setSelectedManufacturingLine(() => null); }}
          className="btn simple-btn"
          style={{ marginBottom: '12px' }}
        >
          Go Back
        </button>
      );
    }

    if (selectedManufacturingLineList) {
      return (
        <button
          onClick={() => { clearSelectedSolution(index); setSelectedManufacturingLineList(() => null); }}
          className="btn simple-btn"
          style={{ marginBottom: '12px' }}
        >
          Go Back
        </button>
      );
    }

    return null;
  };

  // Tick / untick a type in a machine filter (setSelected is the row's state setter); the fetch effect
  // re-runs the listing.
  const toggleFilterType = (setSelected, type) => {
    setSelected((previous) => (
      previous.includes(type) ? previous.filter((each) => each !== type) : [...previous, type]
    ));
  };

  // One filter row: a bold label, then a checkbox per option. labelKeyFor maps an option to its i18n key.
  const renderFilterRow = (rowLabelKey, options, selected, setSelected, labelKeyFor) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 'bold', color: '#555', minWidth: '160px' }}>{t(rowLabelKey)}:</span>
      {options.map((option) => (
        <label key={option} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input type="checkbox" checked={selected.includes(option)} onChange={() => toggleFilterType(setSelected, option)} />
          <span>{t(labelKeyFor(option))}</span>
        </label>
      ))}
    </div>
  );

  // The machine filters, shown above the Manufacturing Line listing (a machines-first section with no line
  // picked): row 1 Printing Machines, row 2 Die Cutting Machines.
  const renderMachinesFilter = () => {
    if (!isManufacturingLineSection || selectedManufacturingLine) { return null; }

    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '0 auto 16px', maxWidth: '760px' }}>
          {renderFilterRow('quote_builder.printing_machines', PRINTING_TYPE_FILTER_OPTIONS, selectedPrintingTypes, setSelectedPrintingTypes, (type) => `quote_builder.${type}`)}
          {renderFilterRow('quote_builder.die_cutting_machines', DIE_CUTTING_TYPE_FILTER_OPTIONS, selectedDieCuttingTypes, setSelectedDieCuttingTypes, (type) => `quote_builder.die_cutting_type_${type}`)}
          {/* Admin-only debugging: turning a filter off disables it server-side so the manufacturing lines it
              hides reappear, to check the rule is defined correctly by the business. The "(admin-only)" marker
              tells an admin these are not part of the customer-facing flow. */}
          {window.current_user_admin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontStyle: 'italic', color: '#999' }}>{t('quote_builder.admin_only')}</span>
              {ADMIN_MANUFACTURING_LINE_FILTERS.map(({ key, labelKey, labelParams }) => (
                <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#888' }}>
                  <input
                    type="checkbox"
                    checked={!disabledLineFilters.includes(key)}
                    onChange={() => toggleLineFilter(key)}
                  />
                  <span>{t(labelKey, labelParams ? labelParams() : undefined)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {/* Separate the filter from the Manufacturing Line listing below. */}
        <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '0 0 16px' }} />
      </>
    );
  };

  const CombinationsContent = () => {
    const hasMoreThanShowMoreCount = navSectionCombinations.length > show_more_count;

    // Show button if we have more items than show_more_count and showAll is false
    const shouldShowMoreButton = hasMoreThanShowMoreCount && !showAllRef.current;

    const handleToggleMissingMachines = () => {
      hideMissingMachinesRef.current = !hideMissingMachinesRef.current;
      sortDivs();
    };

    // Largest machine sheet dimension across all listed ManufacturingLines (every group) — the common
    // scale so the per-machine size rectangles on the cards are comparable to each other (a bigger machine
    // renders bigger), and comparable across groups too.
    const allManufacturingLines = manufacturingLineGroups.flatMap((group) => group.manufacturing_lines);
    // Include any restored single line's own sheets, so its reused card (shown on top of the solutions) still
    // scales when the groups that would otherwise set the scale are not loaded (a shared-link line restore).
    const scaleManufacturingLines = selectedManufacturingLine ? [...allManufacturingLines, selectedManufacturingLine] : allManufacturingLines;
    const maxMachineDimensionMm = Math.max(1, ...scaleManufacturingLines.flatMap((manufacturingLine) => [
      manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm,
      manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm
    ].map((value) => parseFloat(value) || 0)));

    // A list entry references a line by { identifier, group_index }. The full line object -- with its
    // priced_solution_count patched in and its sub_section label -- lives in manufacturingLineGroups, so
    // resolve it from there. Returns null if the referenced group/line is missing (a defensive guard; the
    // server builds the lists from the same groups it returns).
    const resolveManufacturingLineListEntry = ({ identifier, group_index }) => {
      const group = manufacturingLineGroups[group_index];
      if (!group) { return null; }

      const line = group.manufacturing_lines.find((candidate) => candidate.identifier === identifier);
      if (!line) { return null; }

      return { line, sectionLabel: group.label, groupIndex: group_index };
    };

    // The material sheets a plan can run, de-duplicated across its parts (a two-part plan usually runs the
    // same sheets), shown once under the machines.
    const renderListMaterialDimensions = (entries) => {
      const seenDimensionIds = new Set();
      const dimensions = [];
      entries.forEach(({ line }) => (line.material_layout_dimensions || []).forEach((dimension) => {
        if (!seenDimensionIds.has(dimension.id)) { seenDimensionIds.add(dimension.id); dimensions.push(dimension); }
      }));
      if (dimensions.length === 0) { return null; }

      return (
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{t('quote_builder.material_dimensions')}:</div>
          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px' }}>
            {dimensions.map((dimension) => (
              <span key={dimension.id} style={{ fontSize: '12px', color: '#555', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px', backgroundColor: '#f9f9f9' }}>
                {window.user_unit == 'in' ? dimension.description_in : dimension.description_mm}
              </span>
            ))}
          </div>
        </div>
      );
    };

    // The priced-solution count per part (each is a per-part figure), or a small spinner until that part's
    // count lands.
    const renderListPricedCounts = (entries) => (
      <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px' }}>
        {entries.map(({ line, sectionLabel, groupIndex }) => (
          <div key={`${groupIndex}:${line.identifier}`} style={{ marginTop: '2px' }}>
            {typeof line.priced_solution_count === 'number' ? (
              <span>{sectionLabel} — {t('quote_builder.priced_solutions')}: <strong>{line.priced_solution_count}</strong></span>
            ) : (
              <span className={styles.cardCountSpinner} />
            )}
          </div>
        ))}
      </div>
    );

    // A plan's distinct machines of one kind (printing or die-cutting), in first-seen order. A machine two
    // parts share -- commonly the die-cutter, now that the finder keeps the smallest of each type -- appears
    // once, so the plan reads as its distinct machines all together.
    const distinctPlanMachines = (entries, { idKey, labelKey, typeKey, widthKey, heightKey }) => {
      const seen = new Set();
      const machines = [];
      entries.forEach(({ line }) => {
        const id = line[idKey];
        if (id == null || seen.has(id)) { return; }
        seen.add(id);
        machines.push({ id, label: line[labelKey], typeLabel: line[typeKey], widthMm: line[widthKey], heightMm: line[heightKey] });
      });
      return machines;
    };

    // One column of the plan's machine grid: the machines of one kind stacked (flex: 0 0 auto so a lone
    // printing machine opposite two die-cutters keeps its natural height and aligns to the top).
    const renderMachineStack = (machines, label, kind) => (
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {machines.map((machine) => (
          <MachineColumn
            key={machine.id}
            label={label}
            machineName={machine.label}
            machineTypeLabel={machine.typeLabel}
            widthMm={machine.widthMm}
            heightMm={machine.heightMm}
            kind={kind}
            maxMachineDimensionMm={maxMachineDimensionMm}
            style={{ flex: '0 0 auto' }}
          />
        ))}
      </div>
    );

    // One Manufacturing Line List rendered as a SINGLE selectable card (one 25% column wide; 4 to a row).
    // A single-line plan (a combined line, or a single-part section) is one machine block. A multi-part plan
    // shows every part's badge together at the top, then the plan's DISTINCT machines grouped into a printing
    // column and a die-cutting column (a shared die-cutter listed once, opposite its presses), then each
    // part's material sheets and priced-solution count. Clicking the card selects the plan.
    const renderManufacturingLineList = (list) => {
      const entries = list.map(resolveManufacturingLineListEntry).filter(Boolean);
      if (entries.length === 0) { return null; }

      const listKey = manufacturingLineListKey(list);
      const selected = manufacturingLineListKey(selectedManufacturingLineList) === listKey;

      const singleLine = entries.length === 1;
      const printingMachines = distinctPlanMachines(entries, { idKey: 'printing_machine_id', labelKey: 'printing_machine_label', typeKey: 'printing_machine_type_label', widthKey: 'printing_sheet_width_mm', heightKey: 'printing_sheet_height_mm' });
      const dieCuttingMachines = distinctPlanMachines(entries, { idKey: 'die_cutting_machine_id', labelKey: 'die_cutting_machine_label', typeKey: 'die_cutting_machine_type_label', widthKey: 'sheet_width_mm', heightKey: 'sheet_height_mm' });

      return (
        <div
          key={listKey}
          className={styles.combinations_grid_item_cont}
          data-manufacturing-line-list-key={listKey}
          data-selected={selected ? 'true' : 'false'}
          onClick={() => setSelectedManufacturingLineList(list)}
          style={{
            display: 'flex', flexDirection: 'column', boxSizing: 'border-box', cursor: 'pointer',
            flex: '0 0 calc(25% - 16px)', maxWidth: 'calc(25% - 16px)',
            margin: '8px', padding: '12px 8px',
            ...(selected ? { borderColor: '#F44336', boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.25)' } : {})
          }}
        >
          {singleLine ? (
            <ManufacturingLineCard
              manufacturingLine={entries[0].line}
              maxMachineDimensionMm={maxMachineDimensionMm}
              sectionLabel={entries[0].sectionLabel}
              bare
              widthStyle={{ width: '100%', maxWidth: 'none', margin: 0 }}
            />
          ) : (
            <>
              {/* Every part this plan makes, badged together at the top. */}
              <div className={styles.part_badges_container} style={{ justifyContent: 'center', marginBottom: '12px' }}>
                {entries.map(({ sectionLabel, groupIndex, line }) => (
                  <span key={`${groupIndex}:${line.identifier}`} className={styles.efficiency_badge}>{sectionLabel}</span>
                ))}
              </div>
              <div className={styles.layout_columns_container}>
                {renderMachineStack(printingMachines, t('layouts.layout_type_printing'), 'printing')}
                {renderMachineStack(dieCuttingMachines, t('layouts.layout_type_die_cutting'), 'die_cutting')}
              </div>
              {renderListMaterialDimensions(entries)}
              {renderListPricedCounts(entries)}
            </>
          )}
        </div>
      );
    };

    // The picked line / plan, reused as its own card centered on top of the solutions (the SAME card the
    // listing shows), with a divider under it — so the user always sees WHICH machine configuration produced
    // the solutions below, in the familiar card form. "Go Back" (above) returns to the listing.
    const renderSelectedManufacturingCard = () => {
      const card = selectedManufacturingLineList
        ? renderManufacturingLineList(selectedManufacturingLineList)
        : (selectedManufacturingLine && selectedManufacturingLine.type
          ? <ManufacturingLineCard manufacturingLine={selectedManufacturingLine} maxMachineDimensionMm={maxMachineDimensionMm} />
          : null);
      if (!card) { return null; }

      return (
        <>
          <div style={{ display: 'flex', justifyContent: 'center' }}>{card}</div>
          <hr />
        </>
      );
    };

    const showingManufacturingLineLists = !selectedManufacturingLine && !selectedManufacturingLineList && manufacturingLineLists.length > 0;

    return (
      <div style={{ position: 'relative' }}>
        <ProgressBarOverlay />
        {renderBackToMachines()}
        {renderSelectedManufacturingCard()}
        {showingManufacturingLineLists
          ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              {manufacturingLineLists.map((list) => renderManufacturingLineList(list))}
            </div>
          )
          : (
            <div ref={myRef} className={styles.combinations_grid}>
              {navSectionCombinations.map((list, index) => (
                <React.Fragment key={index}>
                  {showNavSectionCombinationGroup(list)}
                </React.Fragment>
              ))}
            </div>
          )}

        <div className={styles.show_more_container}>
          {shouldShowMoreButton && (
            <button
              onClick={() => {
                showAllRef.current = true;
                sortDivs();
              }}
              className={`${styles.show_more_button} ${randomString}`}
            >
              {t('quote_builder.show_more')}
            </button>
          )}

          <button
            onClick={handleToggleMissingMachines}
            className={`${styles.show_missing_machines_button} missing-machine-button`}
            style={{ display: 'none' }}
          >
            {t('quote_builder.hide_missing_machines', { count: 0 })}
          </button>
        </div>
      </div>
    );
  };

  // useEffect starts
  // Depend on the selected line's slug, not the object: a null<->slug change (pick / back / restore)
  // must re-search, but merely filling in the full object for the same slug
  // (upgradeSelectedManufacturingLineFrom) must not trigger a redundant second search.
  // Run every section's fetch through the serial queue so only one heavy solve runs at a time (see
  // SectionFetchQueue). The queued task starts the fetch and holds its turn until the fetch settles
  // (releaseQueueSlot, driven by the loading-watch effect below) or a safety timeout fires.
  useEffect(() => {
    let cancelled = false;
    runExclusive(() => new Promise((resolve) => {
      if (cancelled) { resolve(); return; }
      settleQueueSlotRef.current = resolve;
      const started = fetchCombinations();
      if (!started) { releaseQueueSlot(); return; }
      settleTimeoutRef.current = setTimeout(releaseQueueSlot, SECTION_FETCH_TIMEOUT_MS);
    }));
    return () => { cancelled = true; };
    // Each filter array is joined to a stable string so a change (a new array each toggle) re-fetches.
  }, [ count, materialLayoutDimensions, selectedManufacturingLine?.slug, manufacturingLineListKey(selectedManufacturingLineList), selectedPrintingTypes.join(','), selectedDieCuttingTypes.join(','), disabledLineFilters.join(',') ])

  // A fetch has settled once loading returns to false; release this section's queue turn for the next one.
  useEffect(() => {
    if (!loading) { releaseQueueSlot(); }
  }, [ loading ])

  useEffect(sortDivs, [ ])

  // useEffect ends

  // Check if this section gets its solution from another section
  const userQuoteNavSection = window.user_quote_nav_sections?.find(
    section => section.sort_order === index
  );

  if (userQuoteNavSection?.solution_user_quote_nav_section_id) {
    return null;
  }

  if (errorMessage) {
    return (
      <div style={{ padding: '12px', color: '#cf3f12', fontWeight: 'bold' }}>
        {errorMessage}
      </div>
    );
  }

  if(notFound) {
    return (
      <div>
        {renderBackToMachines()}
        {renderMachinesFilter()}
        <div>{t(notFoundMessageKey)}</div>
      </div>
    )
  }

  // Progress bar for parts mode scoring
  const ScoringProgressBar = () => {
    if (scoringProgress.total === 0) {
      return null;
    }

    return (
      <div className={styles.progressOverlay}>
        <div className={styles.progressCard}>
          <div className={styles.progressTitle}>
            {t('quote_builder.loading')}
          </div>

          <div className={styles.progressBarContainer}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${scoringProgress.percentage}%` }}
            >
              <span className={styles.progressPercentage}>
                {scoringProgress.percentage}%
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // The count shown on top of the listing: the Manufacturing Line cards when listing machines (labelled
  // "Manufacturing Lines"), otherwise the solution cards for a picked line (labelled "Solutions").
  const renderSolutionCount = () => {
    const showingManufacturingLineLists = !selectedManufacturingLine && !selectedManufacturingLineList && manufacturingLineLists.length > 0;
    const count = showingManufacturingLineLists ? manufacturingLineLists.length : navSectionCombinations.length;
    if (count === 0) { return null; }

    const labelKey = showingManufacturingLineLists ? 'quote_builder.manufacturing_lines' : 'quote_builder.solutions';
    return (
      <div style={{ textAlign: 'center', color: '#555', fontWeight: 'bold', margin: '0 0 12px' }}>
        {t(labelKey)}: {count}
      </div>
    );
  };

  return (
    <ProgressProvider>
      <div>
        {/* The Printing Machines filter sits above the listing and stays visible while a filter change
            re-fetches (loading), so the checkbox you just clicked does not disappear. */}
        {renderMachinesFilter()}

        {/* Show progress bar during parts mode scoring */}
        <ScoringProgressBar />

        {/* Show loading overlay only when not scoring. When a ManufacturingLine is already
            selected (picked from a card or restored from the URL) we are loading that line's
            solutions, not searching the floor, so the message reflects that. */}
        {loading && scoringProgress.total === 0 && (
          <LoadingOverlay
            message={selectedManufacturingLine
              ? t('quote_builder.loading_manufacturing_line')
              : t('quote_builder.finding_manufacturing_lines')}
          />
        )}

        {!loading && renderSolutionCount()}
        {!loading && <CombinationsContent />}

      </div>
    </ProgressProvider>
  )
}

export default LayoutNavSectionCombinationList;
